import { stripHtmlTags } from '@change-corgi/core/html';

import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

import type { DecisionMakers as ApiDms } from '../../../../hooks/decisionMakers';

import type { DecisionMaker } from './index';

export type AllDmResponses = Array<{
	id: string;
	message: string;
	decisionMaker: { id: string; displayName: string };
}>;

const PRIMARY_ROLE_KEYWORDS = [
	'president',
	'senator',
	'representative',
	'legislature',
	'executive',
	'chair',
	'chairperson',
	'governor',
	'attorney general',
	'commission',
	'commissioner',
	'supervisor',
	'district attorney',
	'sheriff',
	'judge',
	'mayor',
	'council',
	'alderman',
	'chief',
];

export function sortIndividualDms(dms: DecisionMaker[]): DecisionMaker[] {
	return [...dms].sort((a, b) => {
		// First prioritize responses
		const hasResponseA = !!a.response;
		const hasResponseB = !!b.response;
		if (hasResponseA !== hasResponseB) {
			return hasResponseB ? 1 : -1;
		}

		// Next prioritize highest constituentCounts
		const countA = parseInt((a.constituentCount || '0').replace(/,/g, ''), 10) || 0;
		const countB = parseInt((b.constituentCount || '0').replace(/,/g, ''), 10) || 0;
		return countB - countA;
	});
}

export function getConstituencyStat(constituentCount: string | null | undefined): string {
	if (!constituentCount) return '';
	return `Represents ${constituentCount} petition ${constituentCount === '1' ? 'signer' : 'signers'}`;
}

export function normalizeDms(dms: ApiDms, allDmResponses: AllDmResponses): DecisionMaker[] {
	return dms.map((dm) => {
		const dmResponse = allDmResponses.find((response) => response.decisionMaker.id === dm.id);
		const constituentCount = dm.targetPetitionAssociationData?.constituentCountAmongSignersLocalized;

		return {
			id: dm.id,
			displayName: dm.displayName,
			slug: dm.slug,
			title: dm.title,
			location: dm.state ? getUsState(dm.state) : null,
			photoUrl: dm.photo?.userMedium.url ? dm.photo.userMedium.url : null,
			constituentCount,
			signersText: getConstituencyStat(constituentCount),
			publiclyVisible: dm.publiclyVisible,
			...(dmResponse?.message ? { response: stripHtmlTags(dmResponse.message) } : {}),
		};
	});
}

export function getPrimaryDms(politicians: ApiDms): ApiDms {
	const primaries = politicians.filter((dm) => {
		if (!dm.title) return false;
		const title = dm.title.toLowerCase();
		return PRIMARY_ROLE_KEYWORDS.some((role) => title.includes(role));
	});
	return primaries;
}
